import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, IconButton } from '@/components/ui';
import {
  ArtifactPanelShell,
  FilePreviewContent,
  type HtmlPreviewMode,
  HtmlPreviewModeToggle,
  useCopyFileText,
} from '@/features/artifacts';
import { CanvasExportMenu } from '@/features/canvas/CanvasExportMenu';
import { SharePublishedSitePopover } from '@/features/published-sites/SharePublishedSitePopover';
import { SandboxTerminalToggle } from '@/features/sprites/SandboxTerminalToggle';
import { useEdgeResize } from '@/hooks/useEdgeResize';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { downloadFileWithFeedback } from '@/utils/file-io';
import {
  getDisplayFilename,
  isCanvasFile,
  isHtmlFile,
  isMarkdownFile,
  stripFileExtension,
} from '@/utils/files';

import { CHAT_MIN_WIDTH } from './chat-row-layout.store';
import {
  filePreviewPanelStore,
  MAX_PREVIEW_WIDTH,
  MIN_PREVIEW_WIDTH,
} from './file-preview-panel.store';

interface FilePreviewSidebarProps {
  chatId: string;
  insetTop?: boolean;
}

export const FilePreviewSidebar = observer(function FilePreviewSidebar({
  chatId,
  insetTop = false,
}: FilePreviewSidebarProps) {
  const { files: filesStore } = useStores();
  const isOpen =
    filePreviewPanelStore.isOpen && filePreviewPanelStore.threadId === chatId;
  const fileId = filePreviewPanelStore.fileId;
  const file = fileId ? filesStore.getById(fileId) : undefined;
  const displayName = file ? getDisplayFilename(file.filename) : '';
  const displayNameNoExt = stripFileExtension(displayName);
  const isCanvas = file ? isCanvasFile(file.contentType, file.filename) : false;
  const isHtml = file ? isHtmlFile(file.contentType, file.filename) : false;
  const isMarkdown = file
    ? isMarkdownFile(file.contentType, file.filename)
    : false;
  const canShare = isCanvas || isHtml;
  const { isFullscreen, isFloating, canDock, dockedWidth } =
    filePreviewPanelStore;

  const { isDragging, onResizeStart } = useEdgeResize({
    axis: 'x',
    min: MIN_PREVIEW_WIDTH,
    max: () =>
      Math.min(
        MAX_PREVIEW_WIDTH,
        filePreviewPanelStore.availableWidth > 0
          ? filePreviewPanelStore.availableWidth - CHAT_MIN_WIDTH
          : window.innerWidth * 0.8
      ),
    getStartSize: () => filePreviewPanelStore.dockedWidth,
    onResize: (next) => filePreviewPanelStore.setWidth(next),
  });

  const [htmlViewMode, setHtmlViewMode] = useState<HtmlPreviewMode>('preview');
  useEffect(() => {
    setHtmlViewMode('preview');
  }, [isOpen, fileId]);

  const [reloadKey, setReloadKey] = useState(0);
  const { copied, copyFileText } = useCopyFileText('FilePreviewSidebar');

  return (
    <AnimatePresence>
      {isOpen && file && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={isFloating ? undefined : { width: dockedWidth }}
          className={cn(
            'h-full pr-2 pb-2',
            isFloating ? 'absolute inset-0 z-30 pl-2' : 'relative shrink-0',
            insetTop && 'pt-2'
          )}
          data-testid="file-preview-sidebar"
        >
          {!isFloating && (
            <div
              onMouseDown={onResizeStart}
              className={cn(
                'group absolute left-0 bottom-2 z-10 flex w-1.5 cursor-ew-resize justify-start',
                insetTop ? 'top-2' : 'top-0'
              )}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize preview"
            >
              <div
                className={cn(
                  'h-full w-0 bg-border-strongest transition-all',
                  'group-hover:w-0.5',
                  isDragging && 'w-0.5'
                )}
              />
            </div>
          )}
          <ArtifactPanelShell
            title={displayNameNoExt}
            leading={
              <Icons.file className="ml-2 size-4 text-foreground-muted shrink-0" />
            }
            actions={
              <>
                {isHtml && userStore.isInternalUser && (
                  <HtmlPreviewModeToggle
                    mode={htmlViewMode}
                    onModeChange={setHtmlViewMode}
                  />
                )}
                {canShare && (
                  <SharePublishedSitePopover target={{ fileId: file.id }}>
                    <Button
                      size="sm"
                      variant="secondary"
                      aria-label="Share artifact"
                    >
                      Share
                    </Button>
                  </SharePublishedSitePopover>
                )}
              </>
            }
            iconActions={
              <>
                <IconButton
                  size="sm"
                  icon={Icons.refresh}
                  tooltip="Reload"
                  aria-label="Reload preview"
                  onClick={() => setReloadKey((key) => key + 1)}
                />
                <SandboxTerminalToggle chatId={chatId} />
                {isMarkdown && (
                  <IconButton
                    size="sm"
                    icon={Icons.copy}
                    tooltip={copied ? 'Copied to clipboard' : 'Copy'}
                    aria-label="Copy markdown"
                    onClick={() => void copyFileText(file)}
                  />
                )}
                {isCanvas ? (
                  <CanvasExportMenu file={file} />
                ) : (
                  <IconButton
                    size="sm"
                    icon={Icons.download}
                    tooltip="Download"
                    aria-label="Download file"
                    onClick={() =>
                      void downloadFileWithFeedback(
                        filesStore,
                        file,
                        'FilePreviewSidebar'
                      )
                    }
                  />
                )}
                {canDock && (
                  <IconButton
                    size="sm"
                    icon={isFullscreen ? Icons.minimize : Icons.maximize}
                    tooltip={isFullscreen ? 'Exit full screen' : 'Full screen'}
                    aria-label={
                      isFullscreen
                        ? 'Exit full screen'
                        : 'Expand to full screen'
                    }
                    onClick={() => filePreviewPanelStore.toggleFullscreen()}
                  />
                )}
                <IconButton
                  size="sm"
                  icon={Icons.cancel}
                  tooltip="Close"
                  aria-label="Close preview"
                  onClick={() => filePreviewPanelStore.close()}
                />
              </>
            }
          >
            <div className="flex-1 overflow-hidden">
              <FilePreviewContent
                key={`${file.id}-${reloadKey}`}
                file={file}
                chatId={chatId}
                htmlViewMode={htmlViewMode}
              />
            </div>
          </ArtifactPanelShell>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
